import { useState } from "react";
import { Bell, X, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Daily Reminder banner — nudges the user to update their Today's Focus.
 * Design matches the user-supplied screenshot: soft lavender pill, purple
 * bell circle on the left, sparkle-accented heading, dismiss X + "Got it!"
 * pill button.
 *
 * Dismissal is persisted per day so it doesn't nag the same person twice
 * on the same date.
 */
export default function DailyReminderBanner() {
  const todayKey = new Date().toISOString().slice(0, 10);
  const dismissKey = `lumivera_daily_reminder_dismissed_${todayKey}`;
  const [dismissed, setDismissed] = useState(
    () => localStorage.getItem(dismissKey) === "1"
  );

  if (dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(dismissKey, "1");
    setDismissed(true);
  };

  return (
    <div
      className="relative rounded-3xl px-4 py-4 sm:px-5 sm:py-5 flex items-center gap-3 sm:gap-4"
      style={{
        backgroundColor: "hsl(var(--primary) / 0.08)",
        border: "1px solid hsl(var(--primary) / 0.18)",
      }}
      data-testid="daily-reminder-banner"
    >
      {/* Bell icon circle */}
      <div
        className="shrink-0 rounded-full flex items-center justify-center size-11 sm:size-12"
        style={{ backgroundColor: "hsl(var(--primary))" }}
      >
        <Bell
          strokeWidth={2}
          className="size-5 sm:size-6"
          style={{ color: "white" }}
          fill="white"
        />
      </div>

      {/* Copy */}
      <div className="flex-1 min-w-0 pr-8 sm:pr-4">
        <div className="flex items-center gap-1.5">
          <div
            className="font-semibold text-sm sm:text-base"
            style={{ color: "hsl(var(--primary))" }}
          >
            Daily Reminder
          </div>
          <Sparkles
            strokeWidth={1.5}
            className="size-3.5 sm:size-4"
            style={{ color: "#E6B84A" }}
            fill="#F5D48A"
          />
        </div>
        <p
          className="text-xs sm:text-sm mt-0.5 leading-snug"
          style={{ color: "hsl(var(--primary) / 0.85)" }}
        >
          Update your Today&apos;s Focus each day to stay inspired and on track!
        </p>
      </div>

      {/* Got it — hidden on very small screens to keep the row from wrapping */}
      <Button
        onClick={dismiss}
        size="sm"
        className="hidden sm:inline-flex rounded-full h-9 px-5 shrink-0 text-sm font-semibold"
        style={{
          backgroundColor: "hsl(var(--primary))",
          color: "white",
        }}
        data-testid="daily-reminder-got-it"
      >
        Got it!
      </Button>

      {/* Dismiss × */}
      <button
        onClick={dismiss}
        aria-label="Dismiss reminder"
        className="absolute top-3 right-3 p-1 rounded-md hover:bg-black/5 transition"
        data-testid="daily-reminder-dismiss"
      >
        <X
          strokeWidth={1.5}
          className="size-4"
          style={{ color: "hsl(var(--primary) / 0.6)" }}
        />
      </button>
    </div>
  );
}
